// apps/storefront/src/lib/privacyRuntime.ts
//
// The browser half of the privacy decision. The server renders tenant-level
// facts only — the mode and which vendors are configured — onto
// `<html data-sf-privacy>`, because storefront pages are edge-cached per
// tenant and the HTML must not vary by visitor. This module adds the
// visitor-level inputs (saved choice, GPC) and asks the pure engine.
//
// Config is read from the DOM lazily, on first use, rather than pushed in by
// a component's effect: child effects run before the layout's, so a page
// component tracking on mount (ProductViewBeacon) would otherwise ask an
// unconfigured runtime and be refused. Unconfigured still answers "no".
//
// CLIENT API for anything that wants to set a cookie or load a tag:
//   privacyAllows(purpose)  — may this purpose run right now?
//   mayUseMarketing()       — may a marketing cookie be set (e.g. sf_partner)?
//   onPrivacyChange(cb)     — re-check after the visitor changes their choice
// The server mirror is privacyServer.ts.

import {
  PURPOSE_GROUPS,
  PURPOSES,
  isAllowed,
  noticeVersionFor,
  type NoticeVendor,
  type PrivacyMode,
  type Purpose,
  type PurposeDecision,
  type PurposeGroup,
  type SavedPreference,
} from './privacyDecision'
import { CONSENT_UPDATED_EVENT, announcePreferenceChange, readGpc, readPreference, writePreference } from './consent'

export type PrivacyConfig = {
  tenantId: string
  mode: PrivacyMode
  vendors: readonly NoticeVendor[]
}

let config: PrivacyConfig | null = null
let noticeVersion = ''
/**
 * The choice written in THIS page view — the only copy of it when the browser
 * refused both the cookie and its localStorage fallback. Without it an opt-out in such a context is a silent
 * no-op: nothing stored, every read answers null, and in us_opt_out the
 * engine keeps answering yes while the panel closes as though it worked.
 */
let memoryPreference: SavedPreference | null = null
let memoryPreferencePersisted = true

function configurePrivacy(cfg: PrivacyConfig): void {
  config = cfg
  noticeVersion = noticeVersionFor(cfg)
  memoryPreference = null
  memoryPreferencePersisted = true
}

const MODES: readonly PrivacyMode[] = ['us_opt_out', 'consent_required']
const VENDORS: readonly NoticeVendor[] = ['measurement', 'advertising', 'session_replay']

/** Parses the layout's `data-sf-privacy` attribute. Anything malformed → null (fail closed). */
export function parsePrivacyConfig(raw: string | undefined | null): PrivacyConfig | null {
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as Partial<PrivacyConfig>
    if (typeof v.tenantId !== 'string' || !v.tenantId) return null
    if (!MODES.includes(v.mode as PrivacyMode)) return null
    const vendors = Array.isArray(v.vendors) ? v.vendors.filter((x): x is NoticeVendor => VENDORS.includes(x)) : []
    return { tenantId: v.tenantId, mode: v.mode as PrivacyMode, vendors }
  } catch {
    return null
  }
}

function ensureConfig(): PrivacyConfig | null {
  if (config) return config
  if (typeof document === 'undefined') return null
  const cfg = parsePrivacyConfig(document.documentElement?.dataset?.sfPrivacy)
  if (cfg) configurePrivacy(cfg)
  return config
}

function savedPreference(tenantId: string): SavedPreference | null {
  return memoryPreference ?? readPreference(tenantId)
}

/** Synchronous because `track()` asks on every event. */
export function privacyAllows(purpose: Purpose): boolean {
  const cfg = ensureConfig()
  if (!cfg) return false
  return isAllowed({ purpose, mode: cfg.mode, saved: savedPreference(cfg.tenantId), gpc: readGpc(), noticeVersion })
}

/** May a first-party marketing cookie (e.g. the partner `sf_partner` cookie) be set? */
export function mayUseMarketing(): boolean {
  return privacyAllows('advertising')
}

/** Runs `cb` whenever the visitor saves a choice. Returns the unsubscribe. */
export function onPrivacyChange(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(CONSENT_UPDATED_EVENT, cb)
  return () => window.removeEventListener(CONSENT_UPDATED_EVENT, cb)
}

/** The tenant's mode, or null before config is available. */
export function privacyMode(): PrivacyMode | null {
  return ensureConfig()?.mode ?? null
}

/** Is the browser sending Global Privacy Control? */
export function gpcActive(): boolean {
  return readGpc()
}

/**
 * Did the last choice reach storage? False means its only copy is in this
 * page's memory, so a reload would DISCARD it and re-inject every vendor —
 * AnalyticsScripts skips its revoke-reload then.
 */
export function preferencePersisted(): boolean {
  return memoryPreferencePersisted
}

// .some() is safe only because every writer (savePrivacyChoice, the v1
// migration) gives every purpose in a group the same decision.
export function currentGroupState(): Record<PurposeGroup, boolean> {
  return {
    site_analytics: PURPOSE_GROUPS.site_analytics.some(privacyAllows),
    marketing: PURPOSE_GROUPS.marketing.some(privacyAllows),
  }
}

export function savePrivacyChoice(groups: Record<PurposeGroup, boolean>): void {
  const cfg = ensureConfig()
  if (!cfg) return
  const purposes: Partial<Record<Purpose, PurposeDecision>> = {}
  for (const [group, allowed] of Object.entries(groups) as Array<[PurposeGroup, boolean]>) {
    for (const p of PURPOSE_GROUPS[group]) purposes[p] = allowed ? 'allow' : 'deny'
  }
  // Made while sending GPC → it is the visitor's own word over the signal.
  const { state, persisted } = writePreference(cfg.tenantId, purposes, noticeVersion, readGpc())
  // In place BEFORE the announcement: listeners re-read the verdict inside dispatchEvent.
  memoryPreference = state
  memoryPreferencePersisted = persisted
  announcePreferenceChange(persisted)
}

/**
 * Should the first-visit banner appear? Only in consent mode, only while a
 * purpose is unanswered against the current notice — and never for a visitor
 * sending GPC: asking someone who told their browser to opt out is the dark
 * pattern state enforcement looks for. A GPC visitor can still opt in from the
 * footer panel, on their own initiative.
 */
export function needsPrompt(): boolean {
  const cfg = ensureConfig()
  if (!cfg || cfg.mode !== 'consent_required') return false
  if (readGpc()) return false
  const saved = savedPreference(cfg.tenantId)
  if (!saved || saved.noticeVersion !== noticeVersion) return true
  return PURPOSES.some((p) => saved.purposes[p] === undefined)
}

export const __testPrivacyRuntime = {
  reset: () => {
    config = null
    noticeVersion = ''
    memoryPreference = null
    memoryPreferencePersisted = true
  },
}
